/** 商品详情只提交图片 URL；旧的内嵌图片在保存时逐张转换。 */
export type ImageUploader = (file: File) => Promise<string>;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const MAX_HTML_BYTES = 1024 * 1024;

export async function uploadDetailImage(file: File, upload: ImageUploader): Promise<string> {
  if (!/^image\/(jpeg|png|gif|bmp)$/i.test(file.type))
    throw new Error('详情图片仅支持 JPG、PNG、GIF、BMP');
  if (file.size > MAX_IMAGE_BYTES) throw new Error('单张详情图片不能超过 10 MB');
  const url = await upload(file);
  if (!/^(https?:\/\/|\/[^/])/i.test(url)) throw new Error('上传未返回有效图片地址，请重试');
  // 详情会在 APP/后台等不同域名展示，不能保存仅相对商家端的资源路径。
  return new URL(url, window.location.origin).href;
}

export async function prepareGoodsDescription(
  html: string | null | undefined,
  upload: ImageUploader,
  onProgress?: (html: string) => void,
): Promise<string> {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const cache = new Map<string, string>();
  for (const img of Array.from(doc.querySelectorAll('img'))) {
    const src = img.getAttribute('src') ?? '';
    if (/^data:/i.test(src)) {
      let url = cache.get(src);
      if (!url) {
        const comma = src.indexOf(',');
        const header = src.slice(0, comma);
        if (!/^data:image\/(jpeg|png|gif|bmp);base64$/i.test(header))
          throw new Error('详情中有不支持的内嵌图片，请重新上传');
        if (src.length - comma - 1 > Math.ceil(MAX_IMAGE_BYTES / 3) * 4)
          throw new Error('单张详情图片不能超过 10 MB，请压缩后重新上传');
        const mime = header.slice(5, header.indexOf(';'));
        const binary = atob(src.slice(comma + 1));
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        url = await uploadDetailImage(new File([bytes], 'detail.' + mime.split('/')[1], { type: mime }), upload);
        cache.set(src, url);
      }
      img.setAttribute('src', url);
      onProgress?.(doc.body.innerHTML);
    } else if (/^blob:/i.test(src)) {
      throw new Error('详情中有尚未上传的图片，请重新上传');
    }
  }
  const result = doc.body.innerHTML;
  if (new Blob([result]).size > MAX_HTML_BYTES) throw new Error('商品详情内容不能超过 1 MB，请精简内容');
  return result;
}

interface ImageEditor {
  root: HTMLElement;
  getSelection: (focus: true) => { index: number } | null;
  getLength: () => number;
  insertEmbed: (index: number, type: string, value: string, source: 'api') => unknown;
  setSelection: (index: number, length: number, source: 'silent') => unknown;
  getModule: (name: string) => unknown;
}

/** 捕获文件粘贴/拖入；HTML 中的内嵌图片由保存兜底转换。 */
export function bindDetailImageUpload(
  editor: ImageEditor,
  upload: ImageUploader,
  onBusy: (busy: boolean) => void,
  onError: (message: string) => void,
): () => void {
  let disposed = false;
  let uploading = false;
  const handleFiles = async (files: File[]): Promise<void> => {
    if (uploading || editor.root.getAttribute('contenteditable') === 'false' || !files.length) return;
    uploading = true;
    const index = editor.getSelection(true)?.index ?? Math.max(0, editor.getLength() - 1);
    onBusy(true);
    try {
      let position = index;
      for (const file of files) {
        const url = await uploadDetailImage(file, upload);
        if (disposed) return;
        editor.insertEmbed(position++, 'image', url, 'api');
        editor.setSelection(position, 0, 'silent');
      }
    } catch (error: unknown) {
      if (!disposed) onError(error instanceof Error ? error.message : '图片上传失败，请重试');
    } finally {
      uploading = false;
      if (!disposed) onBusy(false);
    }
  };
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/jpeg,image/png,image/gif,image/bmp';
  input.multiple = true;
  input.onchange = (): void => {
    void handleFiles(Array.from(input.files ?? []));
    input.value = '';
  };
  const toolbar = editor.getModule('toolbar') as { addHandler: (name: string, handler: () => void) => void };
  toolbar.addHandler('image', () => input.click());
  const handlePaste = (event: ClipboardEvent): void => {
    const files = Array.from(event.clipboardData?.files ?? []).filter((file) => file.type.startsWith('image/'));
    if (!files.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void handleFiles(files);
  };
  const handleDrop = (event: DragEvent): void => {
    const files = Array.from(event.dataTransfer?.files ?? []).filter((file) => file.type.startsWith('image/'));
    if (!files.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void handleFiles(files);
  };
  editor.root.addEventListener('paste', handlePaste, true);
  editor.root.addEventListener('drop', handleDrop, true);
  return (): void => {
    disposed = true;
    if (uploading) onBusy(false);
    input.onchange = null;
    editor.root.removeEventListener('paste', handlePaste, true);
    editor.root.removeEventListener('drop', handleDrop, true);
  };
}
